import React, {useEffect, useState} from "react";
import PropTypes from "prop-types";
import {makeStyles} from "@material-ui/core/styles";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import SweetAlert from "react-bootstrap-sweetalert";
import CircularProgress from "@material-ui/core/CircularProgress";
import {useTranslation} from "react-i18next";

//helpers:
import {getFileWithEncodedName} from "../../../../_metronic/_helpers";

const useStyles = makeStyles(theme => ({
    modal: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0, 0, 0, 0.5)",
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        border: "none",
        borderRadius: "0.5rem",
        boxShadow: theme.shadows[5],
    },
}));

export const ImageUploadModal = ({
    id = undefined,
    open = {open: false, type: ""},
    setOpen = () => {},
    onUpload = () => {},
    onSuccess = () => {},
    placeholder = "",
    fileType = "image/*",
}) => {
    const {t} = useTranslation();
    const classes = useStyles();

    const [image, setImage] = useState("");
    const [message, setMessage] = useState("");
    const [showUploadedModal, setShowUploadedModal] = useState(false);
    const [showFailedModal, setShowFailedModal] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleChange = e => {
        if (e.target.files.length) {
            const MAX_SIZE_ALLOWED = 5;
            const size = e.target.files[0].size / 1024 / 1024; // in MB
            if (size > MAX_SIZE_ALLOWED) {
                setMessage(t("MODAL.CREATE_TRACK.EXCEEDS_SIZE"), {
                    size: MAX_SIZE_ALLOWED,
                });
            } else if (e.target.files[0].type.includes(fileType)) {
                setMessage(t("MODAL.CREATE_TRACK.FILETYPENOTMATCH"));
            } else {
                setImage({
                    preview: URL.createObjectURL(e.target.files[0]),
                    raw: e.target.files[0],
                });
            }
        }
    };

    useEffect(() => {
        if (!open.open) {
            setTimeout(() => {
                setImage("");
                setMessage("");
            }, 800);
        }
    }, [open]);

    const handleUpload = () => {
        setLoading(true);
        const picType = open?.type;
        const file = image.raw;
        const fileWithEncodedName = getFileWithEncodedName(file, fileType);

        onUpload({
            id,
            file: fileWithEncodedName,
            picType,
            onConfirmUploaded: () => {
                onSuccess();
                setOpen({open: false, type: ""});
                setShowUploadedModal(true);
                setLoading(false);
            },
            onError: () => {
                setOpen({open: false, type: ""});
                setShowFailedModal(true);
                setLoading(false);
            },
        });
    };

    return (
        <>
            <Modal
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                className={`${classes.modal} uploadmodal`}
                open={open && open.open}
                onClose={() => setOpen({open: false, type: ""})}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={open.open}>
                    <div
                        className={`border-focus-0 pt-4 px-8 pb-6 text-center d-flex flex-column justify-content-center align-items-center ${classes.paper}`}
                    >
                        {loading ? (
                            <div className="w-100 h-100 d-flex justify-content-center align-items-center position-absolute z-index-2">
                                <CircularProgress />
                            </div>
                        ) : null}
                        <div className="py-5 py-lg-6 text-center">
                            <h3 className="text-muted font-weight-bold">{t("MODAL.UPLOAD_IMAGE.TITLE")}</h3>
                        </div>
                        {placeholder || image.preview ? (
                            <div className="d-flex mb-3 justify-content-between" style={{width: 350}}>
                                {placeholder ? (
                                    <div className="d-flex flex-column">
                                        <h5>{t("MODAL.UPLOAD_IMAGE.CURRENT")}</h5>
                                        <div
                                            className="bg-light d-flex felx-column align-items-center justify-content-center"
                                            style={{
                                                outlineColor: `gray`,
                                                outlineStyle: `dashed`,
                                                outlineWidth: `solid`,
                                                textAlign: "center",
                                                height: `150px`,
                                                width: `150px`,
                                            }}
                                        >
                                            <img
                                                style={{maxHeight: "150px", maxWidth: "150px"}}
                                                src={placeholder || "/media/dummy/dummy-img.jpg"}
                                                alt="to be uploaded"
                                            />
                                        </div>
                                    </div>
                                ) : null}
                                {image.preview ? (
                                    <div className="d-flex flex-column">
                                        <h5>{t("MODAL.UPLOAD_IMAGE.NEW")}</h5>
                                        <div
                                            className="bg-light d-flex felx-column align-items-center justify-content-center"
                                            style={{
                                                outlineColor: `gray`,
                                                outlineStyle: `dashed`,
                                                outlineWidth: `solid`,
                                                textAlign: "center",
                                                height: `150px`,
                                                width: `150px`,
                                            }}
                                        >
                                            <img
                                                id="target"
                                                style={{maxHeight: "150px", maxWidth: "150px"}}
                                                src={image.preview || "/media/dummy/dummy-img.jpg"}
                                                alt="to be uploaded"
                                            />
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                        <div className="input-group py-8 w-250px w-md-350px">
                            <label
                                className="custom-file-label elipsis_3lines cursor-pointer"
                                style={{
                                    maxHeight: 62,
                                }}
                                onChange={e => handleChange(e)}
                            >
                                <input type="file" accept={fileType} />
                                {(image && image.raw && image.raw.name) || t("MODAL.UPLOAD_IMAGE.CHOOSE")}
                            </label>
                        </div>
                        <p className="py-4 text-danger">{message}</p>
                        <div className="d-flex justify-content-between w-100">
                            <button
                                className="btn btn-light font-weight-bolder py-4 px-7 my-3"
                                onClick={() => setOpen({open: false, type: ""})}
                            >
                                {t("GENERAL.CANCEL")}
                            </button>
                            <button
                                className="btn btn-primary font-weight-bolder py-4 px-7 my-3 ml-auto"
                                disabled={!image}
                                onClick={handleUpload}
                            >
                                {t("GENERAL.CONFIRM")}
                            </button>
                        </div>
                    </div>
                </Fade>
            </Modal>
            <SweetAlert
                show={showUploadedModal}
                success
                title={t("MODAL.IMAGE.UPLOAD.SUCCESS")}
                confirmBtnText={t("GENERAL.OK")}
                confirmBtnBsStyle="primary"
                onConfirm={() => {
                    setShowUploadedModal(false);
                }}
            />
            <SweetAlert
                show={showFailedModal}
                error
                title={t("MODAL.IMAGE.UPLOAD.ERROR")}
                confirmBtnText={t("GENERAL.OK")}
                confirmBtnBsStyle="primary"
                onConfirm={() => {
                    setShowFailedModal(false);
                }}
            />
        </>
    );
};

ImageUploadModal.propTypes = {
    id: PropTypes.string,
    open: PropTypes.shape({open: PropTypes.bool, type: PropTypes.string}),
    setOpen: PropTypes.func.isRequired,
    onUpload: PropTypes.func,
    placeholder: PropTypes.string,
};
